import React from 'react';
import { usePayPal } from '../contexts/PayPalContext';

export default function PayPalSummaryCards() {
  const { balance, balanceLoading, summary, summaryLoading, connectionStatus, transactions } = usePayPal();
  
  const isSandbox = connectionStatus?.sandbox;
  const hasNoData = !balanceLoading && !summaryLoading && transactions.length === 0;

  const formatCurrency = (amount: number | string | undefined, currency = 'AUD') => {
    const value = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (value === undefined || isNaN(value)) return '-';
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: currency,
    }).format(value);
  };

  // Get primary balance (usually AUD)
  const primaryBalance = balance?.balances?.find(b => b.currency === 'AUD') || balance?.balances?.[0];
  const availableBalance = primaryBalance?.available_balance?.value;
  const totalBalance = primaryBalance?.total_balance?.value;
  const withheldBalance = primaryBalance?.withheld_balance?.value;
  const balanceCurrency = primaryBalance?.currency || 'AUD';

  return (
    <>
      {/* Sandbox Mode Info */}
      {isSandbox && hasNoData && (
        <div className="alert alert-warning d-flex align-items-center mb-5">
          <i className="ki-outline ki-information-5 fs-2hx text-warning me-4"></i>
          <div className="d-flex flex-column">
            <h4 className="mb-1 text-dark">Sandbox Mode - No Test Data</h4>
            <span>
              You're connected to PayPal Sandbox. To see transactions, create test payments in the{' '}
              <a 
                href="https://developer.paypal.com/dashboard/accounts" 
                target="_blank" 
                rel="noopener noreferrer"
                className="fw-bold"
              >
                PayPal Developer Dashboard
              </a>
              . Use a sandbox personal account to send payments to your sandbox business account.
            </span>
          </div>
        </div>
      )}
      
      {/* Live Mode Info - No Data */}
      {!isSandbox && hasNoData && connectionStatus?.connected && (
        <div className="alert alert-info d-flex align-items-center mb-5">
          <i className="ki-outline ki-information-5 fs-2hx text-info me-4"></i>
          <div className="d-flex flex-column">
            <h4 className="mb-1 text-dark">No Transactions Found</h4>
            <span>
              No PayPal transactions found in the last 30 days. Click "Full Sync" to fetch all historical transactions, 
              or check the browser console for any API errors.
            </span>
          </div>
        </div>
      )}
      
      <div className="row g-5 mb-5">
      {/* Current Balance */}
      <div className="col-xl-3 col-md-6">
        <div className="card card-flush h-100">
          <div className="card-header pt-5">
            <div className="card-title d-flex flex-column">
              <div className="d-flex align-items-center">
                <span className="fs-2hx fw-bold text-dark me-2 lh-1">
                  {balanceLoading ? (
                    <span className="spinner-border spinner-border-sm" />
                  ) : (
                    formatCurrency(availableBalance, balanceCurrency)
                  )}
                </span>
              </div>
              <span className="text-gray-400 pt-1 fw-semibold fs-6">Available Balance</span>
            </div>
          </div>
          <div className="card-body pt-2 pb-4 d-flex align-items-end">
            <div className="d-flex flex-column w-100">
              <div className="d-flex justify-content-between fs-6 text-muted mb-1">
                <span>Total Balance:</span>
                <span className="fw-semibold text-gray-800">{formatCurrency(totalBalance, balanceCurrency)}</span>
              </div>
              {withheldBalance && parseFloat(withheldBalance) > 0 && (
                <div className="d-flex justify-content-between fs-6 text-muted">
                  <span>On Hold:</span>
                  <span className="fw-semibold text-warning">{formatCurrency(withheldBalance, balanceCurrency)}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Total Received */}
      <div className="col-xl-3 col-md-6">
        <div className="card card-flush h-100">
          <div className="card-header pt-5">
            <div className="card-title d-flex flex-column">
              <div className="d-flex align-items-center">
                <span className="fs-2hx fw-bold text-success me-2 lh-1">
                  {summaryLoading ? (
                    <span className="spinner-border spinner-border-sm" />
                  ) : (
                    formatCurrency(summary?.totalReceived)
                  )}
                </span>
              </div>
              <span className="text-gray-400 pt-1 fw-semibold fs-6">Total Received</span>
            </div>
          </div>
          <div className="card-body pt-2 pb-4 d-flex align-items-end">
            <div className="d-flex flex-column w-100">
              <div className="d-flex justify-content-between fs-6 text-muted mb-1">
                <span>Fees Paid:</span>
                <span className="fw-semibold text-danger">-{formatCurrency(summary?.totalFees)}</span>
              </div>
              <div className="d-flex justify-content-between fs-6 text-muted">
                <span>Net Received:</span>
                <span className="fw-semibold text-success">{formatCurrency(summary?.totalNet)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Transaction Counts */}
      <div className="col-xl-3 col-md-6">
        <div className="card card-flush h-100">
          <div className="card-header pt-5">
            <div className="card-title d-flex flex-column">
              <div className="d-flex align-items-center">
                <span className="fs-2hx fw-bold text-dark me-2 lh-1">
                  {summaryLoading ? (
                    <span className="spinner-border spinner-border-sm" />
                  ) : (
                    summary?.total || 0
                  )}
                </span>
              </div>
              <span className="text-gray-400 pt-1 fw-semibold fs-6">Total Transactions</span>
            </div>
          </div>
          <div className="card-body pt-2 pb-4 d-flex align-items-end">
            <div className="d-flex align-items-center w-100">
              <div className="d-flex flex-column flex-grow-1 me-4">
                <div className="d-flex fs-6 justify-content-between mb-1">
                  <span className="text-muted">Unmatched</span>
                  <span className="fw-semibold text-warning">{summary?.unmatched || 0}</span>
                </div>
                <div className="d-flex fs-6 justify-content-between">
                  <span className="text-muted">Matched</span>
                  <span className="fw-semibold text-success">{summary?.matched || 0}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Connection Status */}
      <div className="col-xl-3 col-md-6">
        <div className="card card-flush h-100">
          <div className="card-header pt-5">
            <div className="card-title d-flex flex-column">
              <div className="d-flex align-items-center">
                <span className={`bullet bullet-dot h-10px w-10px me-2 ${connectionStatus?.connected ? 'bg-success' : 'bg-secondary'}`}></span>
                <span className="fs-4 fw-bold text-dark">
                  {connectionStatus?.connected ? 'Connected' : 'Disconnected'}
                </span>
                {connectionStatus?.sandbox && (
                  <span className="badge badge-light-warning ms-2">Sandbox</span>
                )}
              </div>
              <span className="text-gray-400 pt-1 fw-semibold fs-6">PayPal Status</span>
            </div>
          </div>
          <div className="card-body pt-2 pb-4 d-flex align-items-end">
            <div className="d-flex flex-column w-100">
              {connectionStatus?.lastSyncAt && (
                <div className="d-flex justify-content-between fs-6 text-muted mb-1">
                  <span>Last Sync:</span>
                  <span className="fw-semibold text-gray-800">
                    {new Date(connectionStatus.lastSyncAt).toLocaleDateString('en-AU', {
                      day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit'
                    })}
                  </span>
                </div>
              )}
              <div className="d-flex justify-content-between fs-6 text-muted">
                <span>Ignored:</span>
                <span className="fw-semibold text-gray-600">{summary?.ignored || 0}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>
    </>
  );
}
